import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Timer, Play, Pause, SkipForward, Settings, Book, 
  BarChart, Coffee, BrainCircuit, CheckCircle2, Bell,
  Volume2, VolumeX, Maximize2, RefreshCw, Edit2
} from 'lucide-react';
import { FocusSession } from '../../services/productivity/types';

interface FocusHubProps {
  focusSessions: FocusSession[];
  onSessionStart: (session: Partial<FocusSession>) => void;
}

interface TimerSettings {
  focusDuration: number;
  breakDuration: number;
  longBreakDuration: number;
  sessionsUntilLongBreak: number;
  autoStartBreaks: boolean;
  autoStartPomodoros: boolean;
  notifications: boolean;
  sound: boolean;
}

export const FocusHub: React.FC<FocusHubProps> = ({
  focusSessions,
  onSessionStart
}) => {
  // Timer State
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [mode, setMode] = useState<'focus' | 'break' | 'longBreak'>('focus');
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [completedSessions, setCompletedSessions] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  // Settings
  const [settings, setSettings] = useState<TimerSettings>({
    focusDuration: 25,
    breakDuration: 5,
    longBreakDuration: 15,
    sessionsUntilLongBreak: 4,
    autoStartBreaks: false,
    autoStartPomodoros: false,
    notifications: true,
    sound: true
  });

  // Learning Progress
  const courses = [
    { name: 'Design Patterns', progress: 65, lastActivity: '2h atrás' },
    { name: 'System Design', progress: 40, lastActivity: 'Hoje' },
    { name: 'AWS Cloud', progress: 80, lastActivity: 'Ontem' }
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isActive && !isPaused) {
      interval = setInterval(() => {
        setTimeLeft((time) => {
          if (time <= 1) {
            handleTimerComplete();
            return 0;
          }
          return time - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isActive, isPaused]);

  const handleTimerComplete = () => {
    if (settings.sound) {
      // Play sound
      new Audio('/notification.mp3').play().catch(console.error);
    }

    if (settings.notifications) {
      // Show notification
      if (Notification.permission === 'granted') {
        new Notification('Timer Complete!', {
          body: `${mode === 'focus' ? 'Time for a break!' : 'Back to focus!'}`,
          icon: '/icon.png'
        });
      }
    }

    if (mode === 'focus') {
      const newCompletedSessions = completedSessions + 1;
      setCompletedSessions(newCompletedSessions);
      
      if (newCompletedSessions % settings.sessionsUntilLongBreak === 0) {
        switchMode('longBreak');
      } else {
        switchMode('break');
      }
    } else {
      switchMode('focus');
    }
  };

  const switchMode = (newMode: 'focus' | 'break' | 'longBreak') => {
    setMode(newMode);
    setIsActive(false);
    setIsPaused(false);

    switch (newMode) {
      case 'focus':
        setTimeLeft(settings.focusDuration * 60);
        break;
      case 'break':
        setTimeLeft(settings.breakDuration * 60);
        break;
      case 'longBreak':
        setTimeLeft(settings.longBreakDuration * 60);
        break;
    }

    const shouldAutoStart = 
      (newMode === 'focus' && settings.autoStartPomodoros) ||
      ((newMode === 'break' || newMode === 'longBreak') && settings.autoStartBreaks);

    if (shouldAutoStart) {
      setIsActive(true);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getProgressColor = () => {
    switch (mode) {
      case 'focus':
        return 'bg-white';
      case 'break':
        return 'bg-white/80';
      case 'longBreak':
        return 'bg-white/60';
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const onStartSession = () => {
    onSessionStart({ duration: timeLeft });
  };

  const onReset = () => {
    setIsActive(false);
    setIsPaused(false);
    setTimeLeft(settings.focusDuration * 60);
  };

  const onResume = () => {
    setIsActive(true);
    setIsPaused(false);
  };

  const onPause = () => {
    setIsActive(false);
    setIsPaused(true);
  };

  const onOpenSettings = () => {
    setShowSettings(true);
  };

  const onChangeTask = () => {
    // Implement task change logic here
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-[28px] font-semibold text-white">Focus Hub</h2>
        <button 
          onClick={onStartSession}
          className="bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200 flex items-center gap-2"
        >
          <Play className="w-4 h-4" />
          Nova Sessão
        </button>
      </div>

      {/* Timer Section */}
      <div className="bg-[rgb(28,28,30)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[rgb(152,152,157)] text-sm">Tempo Restante</div>
            <div className="text-[64px] font-semibold text-white">{formatTime(timeLeft)}</div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={onReset}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reiniciar
            </button>
            <button
              onClick={isPaused ? onResume : onPause}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200 flex items-center gap-2"
            >
              {isPaused ? (
                <>
                  <Play className="w-4 h-4" />
                  Continuar
                </>
              ) : (
                <>
                  <Pause className="w-4 h-4" />
                  Pausar
                </>
              )}
            </button>
          </div>
        </div>

        {/* Settings */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[rgb(152,152,157)] text-sm">Som</div>
              <button
                onClick={() => setSettings(s => ({ ...s, sound: !s.sound }))}
                className="text-white hover:text-[rgb(152,152,157)] transition-all duration-200"
              >
                {settings.sound ? (
                  <Volume2 className="w-4 h-4" />
                ) : (
                  <VolumeX className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[rgb(152,152,157)] text-sm">Duração</div>
              <select 
                value={settings.focusDuration}
                onChange={(e) => setSettings(s => ({ ...s, focusDuration: parseInt(e.target.value) }))}
                className="bg-transparent text-white text-sm focus:outline-none"
              >
                <option value={25}>25min</option>
                <option value={45}>45min</option>
                <option value={60}>60min</option>
              </select>
            </div>
          </div>
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[rgb(152,152,157)] text-sm">Configurações</div>
              <button
                onClick={onOpenSettings}
                className="text-white hover:text-[rgb(152,152,157)] transition-all duration-200"
              >
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-[rgb(28,28,30)] rounded-2xl p-6">
        <h3 className="text-[20px] font-semibold text-white mb-4">Estatísticas</h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4 text-center">
            <div className="text-[rgb(152,152,157)] text-sm mb-1">Hoje</div>
            <div className="text-white text-[20px] font-semibold">{focusSessions.reduce((acc, session) => acc + session.duration, 0)}min</div>
          </div>
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4 text-center">
            <div className="text-[rgb(152,152,157)] text-sm mb-1">Semana</div>
            <div className="text-white text-[20px] font-semibold">{focusSessions.reduce((acc, session) => acc + session.duration, 0)}min</div>
          </div>
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4 text-center">
            <div className="text-[rgb(152,152,157)] text-sm mb-1">Sessões</div>
            <div className="text-white text-[20px] font-semibold">{completedSessions}</div>
          </div>
          <div className="bg-[rgb(44,44,46)] rounded-2xl p-4 text-center">
            <div className="text-[rgb(152,152,157)] text-sm mb-1">Média</div>
            <div className="text-white text-[20px] font-semibold">{focusSessions.reduce((acc, session) => acc + session.duration, 0) / completedSessions}min</div>
          </div>
        </div>
      </div>

      {/* Current Task */}
      {/* <div className="bg-[rgb(28,28,30)] rounded-2xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[rgb(152,152,157)] text-sm mb-1">Tarefa Atual</div>
            <div className="text-white text-[17px]">{currentTask.title}</div>
          </div>
          <button 
            onClick={onChangeTask}
            className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </div>
      </div> */}

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-zinc-900 rounded-lg p-6 w-full max-w-md m-4"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Configurações</h3>
              <div className="space-y-4">
                {/* Timer Durations */}
                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">Duração do Foco (minutos)</label>
                  <input
                    type="number"
                    value={settings.focusDuration}
                    onChange={e => setSettings(s => ({ ...s, focusDuration: parseInt(e.target.value) }))}
                    className="w-full bg-zinc-800 rounded-lg px-4 py-2 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">Duração da Pausa (minutos)</label>
                  <input
                    type="number"
                    value={settings.breakDuration}
                    onChange={e => setSettings(s => ({ ...s, breakDuration: parseInt(e.target.value) }))}
                    className="w-full bg-zinc-800 rounded-lg px-4 py-2 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm text-zinc-400">Duração da Pausa Longa (minutos)</label>
                  <input
                    type="number"
                    value={settings.longBreakDuration}
                    onChange={e => setSettings(s => ({ ...s, longBreakDuration: parseInt(e.target.value) }))}
                    className="w-full bg-zinc-800 rounded-lg px-4 py-2 text-sm"
                  />
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">Auto-iniciar pausas</span>
                    <input
                      type="checkbox"
                      checked={settings.autoStartBreaks}
                      onChange={e => setSettings(s => ({ ...s, autoStartBreaks: e.target.checked }))}
                      className="w-4 h-4"
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">Auto-iniciar pomodoros</span>
                    <input
                      type="checkbox"
                      checked={settings.autoStartPomodoros}
                      onChange={e => setSettings(s => ({ ...s, autoStartPomodoros: e.target.checked }))}
                      className="w-4 h-4"
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">Notificações</span>
                    <input
                      type="checkbox"
                      checked={settings.notifications}
                      onChange={e => setSettings(s => ({ ...s, notifications: e.target.checked }))}
                      className="w-4 h-4"
                    />
                  </label>
                  <label className="flex items-center justify-between">
                    <span className="text-sm text-zinc-400">Som</span>
                    <input
                      type="checkbox"
                      checked={settings.sound}
                      onChange={e => setSettings(s => ({ ...s, sound: e.target.checked }))}
                      className="w-4 h-4"
                    />
                  </label>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
